import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, Building2, DollarSign, Shield, Lightbulb } from 'lucide-react';

const Home = () => {
  const services = [
    {
      icon: <Lightbulb size={32} />,
      title: 'Feasibility & Financial Appraisal',
      description: 'Data-driven project viability assessments from concept stage through to detailed analysis.',
    },
    {
      icon: <DollarSign size={32} />,
      title: 'Cost & Risk Management',
      description: 'Pragmatic cost control and risk mitigation strategies to protect project value.',
    },
    {
      icon: <Shield size={32} />,
      title: 'Procurement Advisory',
      description: 'Independent procurement support to optimise contractor and consultant selection.',
    },
    {
      icon: <Building2 size={32} />,
      title: 'Building Energy Performance',
      description: 'Energy engineering expertise including BER assessments, compliance modeling, and performance optimization.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-[#0d1b2e] text-white overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1574848296471-28f79a036f79)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0d1b2e] via-[#0d1b2e]/95 to-[#0d1b2e]/80" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="max-w-3xl animate-fadeIn">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Engineering-Led Advisory for the Built Environment
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 mb-8 leading-relaxed">
              Supporting property developers and investors with pragmatic, data-driven advisory services from concept through to exit.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button className="bg-[#4a9ece] hover:bg-[#3a8ebe] text-white px-8 py-6 text-lg">
                  Get in Touch
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/services">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#0d1b2e] px-8 py-6 text-lg">
                  Explore Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0d1b2e] mb-4">
              Core Services
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive engineering advisory across the project lifecycle
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <Card 
                key={index} 
                className="border-slate-200 hover:border-[#4a9ece] hover:shadow-lg transition-all duration-300"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardContent className="p-8">
                  <div className="text-[#4a9ece] mb-4">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-[#0d1b2e] mb-3">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button className="bg-[#0d1b2e] hover:bg-[#1a2d45] text-white px-8 py-6">
                View All Services
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0d1b2e] mb-6">
                Independent Engineering Expertise
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Downes Engineering and Consulting provides independent, engineering-led advisory services to property developers, investors, and asset owners across Ireland and the wider EU.
              </p>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Our approach is rooted in technical rigour and commercial pragmatism, delivering advice you can trust at every stage of the development lifecycle.
              </p>
              <Link to="/about">
                <Button variant="outline" className="border-[#0d1b2e] text-[#0d1b2e] hover:bg-[#0d1b2e] hover:text-white">
                  Learn More About Us
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1519662978799-2f05096d3636"
                alt="Engineering precision"
                className="rounded-lg shadow-xl w-full h-[400px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d1b2e]/20 to-transparent rounded-lg" />
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 bg-[#0d1b2e] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              End-to-End Project Support
            </h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              From concept to exit, we deliver value at every stage
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-[#4a9ece] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold">01</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Improve Project Viability</h3>
              <p className="text-slate-300">
                Early-stage feasibility and financial appraisal to ensure projects are commercially sound before commitment.
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-[#4a9ece] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold">02</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Manage Risk</h3>
              <p className="text-slate-300">
                Proactive cost and risk management strategies to protect value throughout project delivery.
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-[#4a9ece] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold">03</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Optimise Asset Performance</h3>
              <p className="text-slate-300">
                Energy performance services to enhance compliance, reduce operational costs, and maximize asset value.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0d1b2e] mb-6">
            Ready to Discuss Your Project?
          </h2>
          <p className="text-xl text-slate-600 mb-8">
            Get in touch to explore how we can support your development from concept to completion.
          </p>
          <Link to="/contact">
            <Button className="bg-[#4a9ece] hover:bg-[#3a8ebe] text-white px-10 py-6 text-lg">
              Contact Us Today
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
